import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import { useValueChurnProbe } from "../utils/loopDebug";
import {
  CreatePostAppointmentMessagePayload,
  PostAppointmentMessage,
  PostAppointmentSettings,
  DEFAULT_POST_APPOINTMENT_SETTINGS,
  UpdatePostAppointmentMessagePayload,
  UpdatePostAppointmentSettingsPayload,
  computeOffsetMinutes,
} from "../types/servicePostAppointment";
import { API_CONFIG, getApiUrl } from "../config/api";
import { useApp } from "./AppContext";

interface ServicePostAppointmentContextType {
  isLoading: boolean;
  error: string | null;
  getSettings: (salonServiceId: string) => Promise<PostAppointmentSettings>;
  updateSettings: (
    salonServiceId: string,
    payload: UpdatePostAppointmentSettingsPayload,
  ) => Promise<PostAppointmentSettings>;
  createMessage: (
    salonServiceId: string,
    payload: CreatePostAppointmentMessagePayload,
  ) => Promise<PostAppointmentMessage>;
  updateMessage: (
    salonServiceId: string,
    messageUuid: string,
    payload: UpdatePostAppointmentMessagePayload,
  ) => Promise<PostAppointmentMessage>;
  deleteMessage: (salonServiceId: string, messageUuid: string) => Promise<void>;
}

const ServicePostAppointmentContext =
  createContext<ServicePostAppointmentContextType | null>(null);

function extractMessage(err: unknown): string {
  if (err instanceof Error && err.message) return err.message;
  return "An unexpected error occurred";
}

/** Backfill offset_minutes when the server omits it (stable sort key). */
function ensureOffsetMinutes(
  e: PostAppointmentMessage,
): PostAppointmentMessage {
  if (typeof e.offset_minutes === "number" && !Number.isNaN(e.offset_minutes)) {
    return e;
  }
  return {
    ...e,
    offset_minutes: computeOffsetMinutes(e.offset_value, e.offset_unit),
  };
}

function normalizeSettings(
  s: PostAppointmentSettings | undefined,
): PostAppointmentSettings {
  if (!s) return { ...DEFAULT_POST_APPOINTMENT_SETTINGS };
  // Tolerate legacy `events` key from older backends.
  const legacyEvents = (s as unknown as { events?: PostAppointmentMessage[] })
    .events;
  const list = s.messages ?? legacyEvents ?? [];
  return {
    ...DEFAULT_POST_APPOINTMENT_SETTINGS,
    ...s,
    messages: list.map(ensureOffsetMinutes),
  };
}

export function ServicePostAppointmentProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchWithAuth } = useApp();

  const getSettings = useCallback(
    async (salonServiceId: string): Promise<PostAppointmentSettings> => {
      try {
        setIsLoading(true);
        setError(null);
        const data:
          | { post_appointment_settings?: PostAppointmentSettings }
          | PostAppointmentSettings = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_POST_APPOINTMENT_SETTINGS,
            "",
            { salonServiceId },
          ),
        );
        if (data && "post_appointment_settings" in (data as any)) {
          return normalizeSettings(
            (data as { post_appointment_settings: PostAppointmentSettings })
              .post_appointment_settings,
          );
        }
        return normalizeSettings(data as PostAppointmentSettings);
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchWithAuth],
  );

  const updateSettings = useCallback(
    async (
      salonServiceId: string,
      payload: UpdatePostAppointmentSettingsPayload,
    ): Promise<PostAppointmentSettings> => {
      try {
        setIsLoading(true);
        setError(null);
        const data:
          | { post_appointment_settings?: PostAppointmentSettings }
          | PostAppointmentSettings = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_POST_APPOINTMENT_SETTINGS,
            "",
            { salonServiceId },
          ),
          { method: "PUT", body: JSON.stringify(payload) },
        );
        if (data && "post_appointment_settings" in (data as any)) {
          return normalizeSettings(
            (data as { post_appointment_settings: PostAppointmentSettings })
              .post_appointment_settings,
          );
        }
        return normalizeSettings(data as PostAppointmentSettings);
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchWithAuth],
  );

  const createMessage = useCallback(
    async (
      salonServiceId: string,
      payload: CreatePostAppointmentMessagePayload,
    ): Promise<PostAppointmentMessage> => {
      try {
        setIsLoading(true);
        setError(null);
        const data:
          | PostAppointmentMessage
          | {
              message?: PostAppointmentMessage;
              event?: PostAppointmentMessage;
            } = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_POST_APPOINTMENT_MESSAGES,
            "",
            { salonServiceId },
          ),
          { method: "POST", body: JSON.stringify(payload) },
        );
        const created =
          (data as { message?: PostAppointmentMessage }).message ??
          (data as { event?: PostAppointmentMessage }).event ??
          data;
        return ensureOffsetMinutes(created as PostAppointmentMessage);
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchWithAuth],
  );

  const updateMessage = useCallback(
    async (
      salonServiceId: string,
      messageUuid: string,
      payload: UpdatePostAppointmentMessagePayload,
    ): Promise<PostAppointmentMessage> => {
      try {
        setIsLoading(true);
        setError(null);
        const data:
          | PostAppointmentMessage
          | {
              message?: PostAppointmentMessage;
              event?: PostAppointmentMessage;
            } = await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_POST_APPOINTMENT_MESSAGE_BY_ID,
            "",
            { salonServiceId, messageUuid },
          ),
          { method: "PUT", body: JSON.stringify(payload) },
        );
        const updated =
          (data as { message?: PostAppointmentMessage }).message ??
          (data as { event?: PostAppointmentMessage }).event ??
          data;
        return ensureOffsetMinutes(updated as PostAppointmentMessage);
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchWithAuth],
  );

  const deleteMessage = useCallback(
    async (salonServiceId: string, messageUuid: string): Promise<void> => {
      try {
        setError(null);
        await fetchWithAuth(
          getApiUrl(
            API_CONFIG.ENDPOINTS.SERVICE_POST_APPOINTMENT_MESSAGE_BY_ID,
            "",
            { salonServiceId, messageUuid },
          ),
          { method: "DELETE" },
        );
      } catch (err) {
        setError(extractMessage(err));
        throw err;
      }
    },
    [fetchWithAuth],
  );

  // [LOOP] churn probe
  useValueChurnProbe("ServicePostAppointment", { isLoading, error });

  // Memoized so consumers don't re-render on every provider render; every
  // method above is useCallback'd, so the deps are all stable references.
  const value: ServicePostAppointmentContextType = useMemo(
    () => ({
      isLoading,
      error,
      getSettings,
      updateSettings,
      createMessage,
      updateMessage,
      deleteMessage,
    }),
    [
      isLoading,
      error,
      getSettings,
      updateSettings,
      createMessage,
      updateMessage,
      deleteMessage,
    ],
  );

  return (
    <ServicePostAppointmentContext.Provider value={value}>
      {children}
    </ServicePostAppointmentContext.Provider>
  );
}

export function useServicePostAppointment() {
  const ctx = useContext(ServicePostAppointmentContext);
  if (!ctx) {
    throw new Error(
      "useServicePostAppointment must be used within a ServicePostAppointmentProvider",
    );
  }
  return ctx;
}
